"use client"

import { ActionIcon, useMantineColorScheme } from '@mantine/core'
import { FiSun, FiMoon, FiMonitor } from 'react-icons/fi'
import { useEffect, useState } from 'react'

export interface ThemeSwitcherProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'filled' | 'outline' | 'light' | 'white' | 'default' | 'transparent' | 'subtle'
  showSystemOption?: boolean
  defaultColorScheme?: 'light' | 'dark' | 'auto'
}

export function ThemeSwitcher({
  size = 'md',
  variant = 'default',
  showSystemOption = true,
  defaultColorScheme = 'auto'
}: ThemeSwitcherProps) {
  const { colorScheme, setColorScheme } = useMantineColorScheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    // Set initial color scheme if not already set
    if (colorScheme === 'auto' && defaultColorScheme !== 'auto') {
      setColorScheme(defaultColorScheme)
    }
  }, [colorScheme, defaultColorScheme, setColorScheme])

  // Prevent hydration mismatch
  if (!mounted) {
    return (
      <ActionIcon
        size={size}
        variant={variant}
        disabled
      >
        <FiSun size="1rem" />
      </ActionIcon>
    )
  }

  const getNextColorScheme = (): 'light' | 'dark' | 'auto' => {
    if (showSystemOption) {
      switch (colorScheme) {
        case 'light':
          return 'dark'
        case 'dark':
          return 'auto'
        case 'auto':
        default:
          return 'light'
      }
    } else {
      return colorScheme === 'light' ? 'dark' : 'light'
    }
  }

  const getIcon = () => {
    switch (colorScheme) {
      case 'light':
        return <FiSun size="1rem" />
      case 'dark':
        return <FiMoon size="1rem" />
      case 'auto':
      default:
        return <FiMonitor size="1rem" />
    }
  }

  const getLabel = () => {
    switch (colorScheme) {
      case 'light':
        return 'Light mode'
      case 'dark':
        return 'Dark mode'
      case 'auto':
      default:
        return 'System theme'
    }
  }

  return (
    <ActionIcon
      onClick={() => setColorScheme(getNextColorScheme())}
      variant={variant}
      size={size}
      aria-label={getLabel()}
      title={getLabel()}
    >
      {getIcon()}
    </ActionIcon>
  )
}
